// components/VocabularyLearning.js
import React, { useState } from 'react';
import SyllablePlayer from './SyllablePlayer';
import { getWordData } from '../data/phoneticData';

const VocabularyLearning = ({ onBack }) => {
  const [currentWord, setCurrentWord] = useState('afternoon');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSyllableBreakdown, setShowSyllableBreakdown] = useState(false);

  const words = ['afternoon', 'father', 'mother', 'grandmother', 'teacher'];
  
  const playSentence = (sentence) => {
    if ('speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      let selectedVoice = voices.find(voice => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.name.includes('Neural'))
      ) || voices.find(voice => voice.lang.startsWith('en-US')) || voices[0];
      
      const utterance = new SpeechSynthesisUtterance(sentence);
      utterance.voice = selectedVoice;
      utterance.rate = 0.6; // Even slower for sentences
      utterance.pitch = 1.0;
      utterance.volume = 0.9;
      utterance.lang = 'en-US';
      
      utterance.onerror = (event) => {
        console.error('Sentence speech error:', event.error);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  };
  
  const playPronunciation = () => {
    setIsPlaying(true);
    if ('speechSynthesis' in window) {
      // Get available voices and select the best quality English voice
      const voices = window.speechSynthesis.getVoices();
      let selectedVoice = voices.find(voice => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.name.includes('Neural'))
      ) || voices.find(voice => voice.lang.startsWith('en-US')) || voices[0];
      
      const utterance = new SpeechSynthesisUtterance(currentWord);
      utterance.voice = selectedVoice;
      utterance.rate = 0.7; // Slower for better clarity
      utterance.pitch = 1.0; // Natural pitch
      utterance.volume = 0.9; // Slightly lower to avoid distortion
      utterance.lang = 'en-US';
      
      // Enhanced audio quality settings
      utterance.onstart = () => {
        console.log('High-quality speech started');
      };
      
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event.error);
        setIsPlaying(false);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  };

  if (showSyllableBreakdown) {
    return (
      <SyllablePlayer 
        word={currentWord} 
        onBack={() => setShowSyllableBreakdown(false)} 
      />
    );
  }

  return (
    <div className="vocabulary-learning">
      <header>
        <button onClick={onBack}>← Back</button>
        <h1>Vocabulary Learning</h1>
      </header>
      
      <div className="word-card">
        <h2>{currentWord}</h2>
        <div className="phonetic">{getWordData(currentWord)?.phonetic || '/ phonetic /'}</div>
        <div className="definition">{getWordData(currentWord)?.definition || 'Definition not available'}</div>
        
        <button 
          className={`play-button ${isPlaying ? 'playing' : ''}`}
          onClick={playPronunciation}
          disabled={isPlaying}
        >
          🔊 {isPlaying ? 'Playing...' : 'Play Pronunciation'}
        </button>
        
        <div className="syllable-breakdown">
          <h3>Syllable Breakdown:</h3>
          <div className="syllables">
            {getWordData(currentWord)?.syllables.map((syllable, index) => (
              <span key={index} className="syllable">
                {syllable.text}
              </span>
            ))}
          </div>
          <button 
            className="breakdown-btn"
            onClick={() => setShowSyllableBreakdown(true)}
          >
            🔍 Detailed Breakdown
          </button>
        </div>
        
        <div className="example-sentence">
          <h3>Example:</h3>
          <p>"{getWordData(currentWord)?.example || 'Example sentence not available.'}"</p>
          <button 
            className="play-sentence"
            onClick={() => playSentence(getWordData(currentWord)?.example || '')}
          >
            🔊 Play Sentence
          </button>
        </div>
        
        <div className="word-exercises">
          <button className="exercise-btn">✏️ Fill in the Blank</button>
          <button className="exercise-btn">🔤 Spelling Practice</button>
          <button className="exercise-btn">🎯 Word Matching</button>
        </div>
      </div>
      
      <div className="word-navigation">
        {words.map((word, index) => (
          <button
            key={word}
            className={`word-nav-btn ${word === currentWord ? 'active' : ''}`}
            onClick={() => setCurrentWord(word)}
          >
            {word}
          </button>
        ))}
      </div>
    </div>
  );
};

export default VocabularyLearning;